import React from 'react'
import PropTypes from 'prop-types'
import axiosInstance from 'api/axios.config'
import EntriesList from '../../models/EntriesList'
import VideoListMobile from './VideoListMobile'
import camelize from 'camelize'

// VideoJS Library
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

// VideoJS Plugins
import videoJsResolutionSelector from '@silvermine/videojs-quality-selector'
import videoJsChromecast from '@silvermine/videojs-chromecast'
import '../../../../node_modules/@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css'

// VideoPlayer React Component Helpers
import ExternalVideo from './ExternalVideo'
import VideoTracker from './VideoTracker'

// VideoPlayer Component Initialization
import {
  initProgressBarAnimation,
  formatTime,
  setupNavButtons,
  setupVideoList
} from './componentSetup'

// Custom Video Components
import './videoComponents/Autoplay'
import './videoComponents/SeekerButtons'
import './videoComponents/PlayButton'
import './videoComponents/VideoList'

// Helper Functions
import extractVideosListProps from './helperFunctions/extractVideosListProps'

window.videojs = videojs // This is because the hotkey plugin looks for videojs from the window object

require('https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1')
require('videojs-hotkeys/videojs.hotkeys')

videoJsResolutionSelector(window.videojs)
videoJsChromecast(window.videojs, { preloadWebComponents: true })

class VideoPlayer extends React.Component {
  constructor (props) {
    super(props)

    if (this.enablePlaylistControls()) {
      let videosListObj = Object.assign({}, camelize(this.props.origin), {
        type: this.props.originType
      })
      this.listEntriesInformation = new EntriesList(videosListObj)
    }

    this.gtmVideoStarted = false
    this.videoNode = React.createRef()

    this.state = {
      seeking: false,
      userInactive: true,
      hasStarted: false,
      playing: false
    }

    this.togglePlay = this.togglePlay.bind(this)
  }

  componentDidMount () {
    if (this.props.asyncFacebook && this.props.video.facebook) {
      window.fbAsyncInit()
    } else if (!this.videoIsUpdating() && !this.isExternalVideo()) {
      this.instantiateVideoPlayer(this.generateVideoOptions())
    }
  }

  instantiateVideoPlayer (videoJsOptions) {
    let player = window.videojs(this.videoNode.current, videoJsOptions, () => {
      formatTime(window.videojs)
      initProgressBarAnimation(player)

      this.setupVolumeAnimation(player)
      this.setupPlayButton(player)
      this.setupSeekerButtons(player)
      this.setupHotkeys(player)

      this.setState({ player })
      this.attachPlayerEvents(player)

      if (this.props.video) {
        this.customizePlayer(player)
        this.disableRightClick(player)
      } else {
        player.src(this.props.src)
      }
    })
  }

  disableRightClick (player) {
    player.contentEl().oncontextmenu = () => {
      return false
    }
  }

  setupHotkeys (player) {
    player.hotkeys({
      volumeStep: 0.1,
      seekStep: 15,
      enableMute: true,
      enableFullscreen: true,
      enableVolumeScroll: false
    })
  }

  customizePlayer (player) {
    this.setupChromecast(player)
    this.setupResolutionSwitcher(player)
    this.setupAutoplay(player)

    if (this.enablePlaylistControls()) {
      setupNavButtons(player, {
        enable: this.enablePlaylistControls(),
        listEntriesInformation: this.listEntriesInformation,
        playlistPos: this.props.playlistPos
      })
      setupVideoList(player, this.videoListProps())
    }
  }

  /**
   * Formats the props object for the videos list component
   * from video player options
   * @return {Object}
   */
  videoListProps () {
    const options = Object.assign({}, this.props, {
      animationHandler: elem => {
        this.attachAnimationHandler(elem)
      },
      enable: this.enablePlaylistControls(),
      planSubscriptionId: this.props.planSubscriptionId,
      listEntriesInformation: this.listEntriesInformation
    })
    return extractVideosListProps(options)
  }

  /**
   * Adds a ball to the video volume control slider
   * @param {*} player
   */
  setupVolumeAnimation (player) {
    const volumeLevelBar = player.$('.vjs-volume-level')
    const volumeModifierBall = document.createElement('div')
    volumeModifierBall.classList.add('vjs-modifier-ball')
    volumeLevelBar.appendChild(volumeModifierBall)
  }

  /**
   * Adds the large play button icon to the center of the video and attaches an
   * OnClick event to it to toggle the video
   * @param {Player} player
   */
  setupPlayButton (player) {
    const PlayButton = window.videojs.getComponent('PlayButton')
    const playButton = new PlayButton(player)
    player.addChild(playButton).addClass('play-icon-container')
    playButton.contentEl().addEventListener('click', () => {
      player.play()
    })
  }

  setupChromecast (player) {
    player.chromecast()
  }

  /**
   * Adds the seeker buttons to the player
   * @param {Player} player
   */
  setupSeekerButtons (player) {
    let options = {}

    if (this.props.video && this.props.trackVideo) {
      options = {
        videoTrackingInformation: this.getVideoMeta()
      }
    }

    let seekerButton = player.getChild('controlBar').addChild('SeekerButtons', options)
    seekerButton.addClass('vjs-seekers')
  }

  /**
   * Configures the autoplay button settings on the control panel.  This method
   * adds the autoplay button to the control panel and plays the video if the video
   * should autoplay
   * @param {Player} player
   */
  setupAutoplay (player) {
    if (this.props.userLoggedIn && !this.props.noAutoplayOption) {
      this.addAutoplayButton(player)
      if (this.props.autoplay) {
        player.play()
        this.setState({ playing: true })
      }
    }
  }

  /**
   * Adds the autoplay button to the video player control panel
   * @param {Player} videoPlayer
   */
  addAutoplayButton (videoPlayer) {
    let autoplayComponent = videoPlayer
      .getChild('controlBar')
      .addChild('Autoplay', {
        autoplay: this.props.autoplay
      })
    autoplayComponent.addClass('vjs-autoplay')
  }

  /**
   * Sets up the resolution selector in the player control panel and adds the
   * resolution selector toggle button to the control panel.  This method
   * also attaches an event to the player for when a user selects a resolution
   * @param {Player} player
   */
  setupResolutionSwitcher (player) {
    let resolutionOptions = this.props.video.resolutions.map(source => ({
      label: `${source.name}p ${source.name === '720' ? 'HD' : ''}`,
      src: source.src,
      res: parseInt(source.name),
      type: this.props.type,
      selected: parseInt(source.name) === this.props.resolution
    }))
    player.src(resolutionOptions)

    let QualitySelector = window.videojs.getComponent('QualitySelector')
    let selector = new QualitySelector(player)

    player.getChild('controlBar').addChild(selector)
    let resolutionSelectorElem = selector.contentEl()
    let selectorBtn = selector.$(
      'button.vjs-menu-button',
      resolutionSelectorElem
    )

    let resolutionSelectorOpen = false

    player.on('qualitySelected', (e, newSource) => {
      this.handleResolutionSelect(newSource)
      selectorBtn.innerHTML = `<span>${
        newSource.res
      }</span> <i class='fa fa-angle-down'></i>`

      if (resolutionSelectorOpen) {
        this.fadeElementOut(resolutionSelectorElem)
        resolutionSelectorOpen = false
      }
    })

    resolutionSelectorElem.onclick = () => {
      if (resolutionSelectorOpen) {
        this.fadeElementOut(resolutionSelectorElem)
      } else {
        this.fadeElementIn(resolutionSelectorElem)
      }
      resolutionSelectorOpen = !resolutionSelectorOpen
    }
  }

  /**
   * Handles the user action whenever a user selects a new resolution by
   * making a POST request to the server to update user resolution preferences
   * @param {Object} newSource
   */
  handleResolutionSelect (newSource) {
    if (this.props.userLoggedIn) {
      // Change this to only make Axios call if user signed in. No need to hammer API with unnecessary calls.
      axiosInstance
        .post('/preferences/video_resolution', {
          video_resolution: newSource.res
        })
        .catch(e => {
          // 401 => User not logged in
          if (e.response && e.response.status !== 401) {
            console.error(e)
          }
        })
    }
  }

  /**
   * Attaches transition classes to an element when for it to fade in
   * and out
   * Timeout numbers are just supposed to be so that there is enough
   * time for the css transitions to take place before moving on to the
   * next state in the transition.
   * @param {DOM Element} element
   * @param {String} activateEvent - Name of event for element to fade in
   * @param {String} hideEvent - Name of event for element to fade out
   */
  attachAnimationHandler (
    element,
    activateEvent = 'click',
    hideEvent = 'click'
  ) {
    let active
    if (activateEvent !== hideEvent) {
      this.attachShowAnimationHandler(element, activateEvent)
      this.attachLeaveAnimationHandler(element, hideEvent)
    } else {
      element.addEventListener(activateEvent, () => {
        if (active) {
          this.fadeElementOut(element)
        } else {
          this.fadeElementIn(element)
        }
        active = !active
      })
    }
  }

  /**
   * Attaches transition classes to an element for it to fade in
   * @param {DOM Element} element
   * @param {String} activateEvent - Name of event for element to fade in
   */
  attachShowAnimationHandler (element, activateEvent = 'click') {
    element.addEventListener(activateEvent, () => {
      if (!this.state.seeking) {
        this.fadeElementIn(element)
      }
    })
  }

  /**
   * Attaches transition classes to an element for it to fade out
   * @param {DOM Element} element
   * @param {String} hideEvent - Name of event for element to fade out
   */
  attachLeaveAnimationHandler (element, hideEvent = 'click') {
    element.addEventListener(hideEvent, () => {
      this.fadeElementOut(element)
    })
  }

  fadeElementIn (element) {
    element.classList.remove('leave-animate-done')
    element.classList.add('enter-animating')
    setTimeout(() => {
      element.classList.add('enter-animate-done')
      element.classList.remove('enter-animating')
    }, 50)
  }

  fadeElementOut (element) {
    element.classList.remove('enter-animate-done')
    element.classList.add('leave-animating')
    setTimeout(() => {
      element.classList.remove('leave-animating')
    }, 300)
  }

  generateVideoOptions () {
    return {
      controls: true,
      controlBar: {
        bigPlayButton: false,
        customControlSpacer: false,
        currentTimeDisplay: true,
        timeDivider: true,
        durationDisplay: true,
        remainingTimeDisplay: false,
        liveControl: false
      },
      poster: this.props.poster || this.props.video.thumbnail.url,
      preload: 'auto',
      techOrder: ['chromecast', 'html5']
    }
  }

  // destroy player on unmount
  componentWillUnmount () {
    if (this.state.player) {
      this.state.player.dispose()
      this.setState({
        player: null
      })
    }
  }

  togglePlay () {
    const { player, playing } = this.state
    playing ? player.pause() : player.play()
    this.setState({ playing: !playing })
  }

  getVideoMeta () {
    return {
      duration: this.props.video.duration,
      introVideo: this.props.introVideo,
      offerId: this.props.offerId,
      planId: this.props.planId,
      planEntryId: this.props.planEntryId,
      sourceId: this.props.sourceId || this.props.planEntryId,
      sourceType: this.props.sourceType || 'PlanEntry',
      videoId: this.props.video.id,
      videoSession: this.props.videoSession
    }
  }

  renderVideo () {
    const { video, trackVideo } = this.props

    let renderedContent
    if (this.isExternalVideo()) {
      renderedContent = <ExternalVideo video={video} />
    } else if (this.videoIsUpdating()) {
      renderedContent = (
        <img src='https://d28gwrkukqy4h7.cloudfront.net/training/videoProcessing.jpg' />
      )
    } else {
      let videoElem = (
        <div data-vjs-player style={{ height: 'auto', width: 'auto' }}>
          <video
            ref={this.videoNode}
            className='video-player video-js vjs-alo-skin'
          />
          <div
            className='icon-container play-icon-container'
            onClick={this.togglePlay}
          />
        </div>
      )

      if (video && trackVideo) {
        renderedContent = (
          <VideoTracker
            videoPlayer={this.state.player}
            videoInfo={this.getVideoMeta()}
            videoHasBeenPlayed={this.state.hasStarted}
          >
            {videoElem}
          </VideoTracker>
        )
      } else {
        renderedContent = videoElem
      }
    }
    return renderedContent
  }

  enablePlaylistControls () {
    return this.props.originType && this.props.userLoggedIn
  }

  isExternalVideo () {
    return (
      this.props.useExternal && this.props.video && this.props.video.external
    )
  }

  videoIsUpdating () {
    return (
      this.props.video &&
      (this.props.video.process_status === 'started' ||
        this.props.video.process_status === 'uploaded')
    )
  }

  attachPlayerEvents (player) {
    player.on('play', () => {
      this.setState({ playing: true, userInactive: false })
    })

    player.on('pause', () => {
      this.setState({ playing: false, userInactive: false })
    })

    player.on('firstplay', () => {
      this.setState({ hasStarted: true })
    })

    player.on('userinactive', () => {
      this.setState({ userInactive: true })
    })

    player.on('useractive', () => {
      this.setState({ userInactive: false })
    })

    player.on('seeking', () => {
      !this.state.seeking && this.setState({ seeking: true })
    })

    player.on('seeked', () => {
      this.setState({ seeking: false })
    })

    // Google Tag Manager
    // Binding to timeupdate event in VideoTracker was not working
    if (this.props.trackVideo && window.dataLayer) {
      player.on('timeupdate', () => {
        if (!this.gtmVideoStarted && player.currentTime() >= 10) {
          this.gtmVideoStarted = true

          window.dataLayer.push({
            event: 'videoStarted',
            productId: `plan_${this.props.planId}`
          })
        }
      })
    }
  }

  render () {
    let videoListMobile
    if (this.enablePlaylistControls()) {
      const controlsShowing =
        this.state.hasStarted &&
        (!this.state.playing || !this.state.userInactive)

      const videoListMobileProps = {
        ...this.videoListProps(),
        controlsShowing,
        playing: this.state.playing
      }

      videoListMobile = <VideoListMobile {...videoListMobileProps} />
    }

    return (
      <div className='alo-video-container'>
        {this.renderVideo()}
        {videoListMobile}
      </div>
    )
  }
}

VideoPlayer.defaultProps = {
  asyncFacebook: false,
  introVideo: false,
  noAutoplayOption: false,
  useExternal: true,
  userLoggedIn: false
}

VideoPlayer.propTypes = {
  autoplay: PropTypes.bool,
  asyncFacebook: PropTypes.bool,
  noAutoplayOption: PropTypes.bool,
  introVideo: PropTypes.bool,
  offerId: PropTypes.number,
  origin: PropTypes.object,
  originType: PropTypes.string,
  poster: PropTypes.string,
  planId: PropTypes.number,
  planEntryId: PropTypes.number,
  planSubscriptionId: PropTypes.number,
  playlistPos: PropTypes.number,
  resolution: PropTypes.number,
  sourceId: PropTypes.number,
  sourceType: PropTypes.string,
  src: PropTypes.string,
  trackVideo: PropTypes.bool,
  type: PropTypes.string,
  useExternal: PropTypes.bool,
  userLoggedIn: PropTypes.bool,
  video: PropTypes.object,
  videoSession: PropTypes.string
}

export default VideoPlayer
